import { useState } from "react";
import { toast } from "sonner";
import { TestTube, ShieldAlert, Microscope, FlaskConical, MessageSquare } from "lucide-react";
import { optPts, gradeChecklist } from "@/data/clinPath";
import { BeatShell, OptionList, ChipRow } from "@/components/nutrition/NutritionParts";
import { ResultsTable } from "@/components/clinpath/ClinPathParts";
import { TubeRack, Centrifuge, ArtefactGallery, BloodFilmScope } from "@/components/clinpath/ClinPathVisuals";

const PatientLine = ({ c }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-700 leading-relaxed" data-testid="clinpath-briefing">
    <span className="font-semibold text-slate-900">{c.name}</span> — {c.breed}, {c.signalment}.
    <div className="mt-1">{c.presentation}</div>
    <div className="mt-1.5 text-[11px] uppercase tracking-wider text-[#b45309] font-semibold">Requested: {c.requested}</div>
  </div>
);

/* ── Beat 1 · Sample handling ─────────────────────────────────────────────── */
export const HandlingBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an option.");
      setRes({ earned: optPts(c.handling.options[idx].score, 20, 10), max: 20, detail: "Tube selection & handling", key: "handling", label: "Sample handling" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={TestTube}
      eyebrow="Stage 1 of 5 · Handling"
      title="Sample handling & tube selection"
      subtitle={c.handling.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="handling-primary-btn"
    >
      <PatientLine c={c} />
      <TubeRack />
      <Centrifuge />
      <OptionList options={c.handling.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="handling-option" />
    </BeatShell>
  );
};

/* ── Beat 2 · Pre-analytical QC ───────────────────────────────────────────── */
export const QcBeat = ({ c, onDone }) => {
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const checked = !!res;

  const toggle = (i) => {
    const n = new Set(sel);
    n.has(i) ? n.delete(i) : n.add(i);
    setSel(n);
  };

  const onPrimary = () => {
    if (!checked) {
      const cl = gradeChecklist(c.qc.checklist, sel);
      setRes({ earned: cl.pts, max: 20, detail: `Problems caught ${cl.caught}/${cl.total}, ${cl.falseTaps} false`, key: "qc", label: "Pre-analytical QC" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={ShieldAlert}
      eyebrow="Stage 2 of 5 · Quality"
      title="Pre-analytical quality check"
      subtitle="Sample artefacts cause more errors than the analyser. Flag every genuine problem."
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="qc-primary-btn"
    >
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{c.qc.prompt}</div>
      <ArtefactGallery />
      <ChipRow chips={c.qc.checklist} selected={sel} onToggle={toggle} revealed={checked} testidPrefix="qc-chip" />
      {checked && (
        <div className="mt-1 space-y-1.5" data-testid="qc-coaching">
          {c.qc.checklist.filter((x) => x.flag).map((x, i) => (
            <div key={i} className="text-[11px] text-slate-600 leading-snug">
              <span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}
            </div>
          ))}
        </div>
      )}
    </BeatShell>
  );
};

/* ── Beat 3 · Haematology ─────────────────────────────────────────────────── */
export const HaemBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an interpretation.");
      setRes({ earned: optPts(c.haem.options[idx].score, 20, 10), max: 20, detail: "Haematology interpretation", key: "haem", label: "Haematology" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Microscope}
      eyebrow="Stage 3 of 5 · Haematology"
      title="Haematology interpretation"
      subtitle={c.haem.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="haem-primary-btn"
    >
      <ResultsTable rows={c.haem.panel} testid="haem-panel" />
      <BloodFilmScope />
      <OptionList options={c.haem.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="haem-option" />
    </BeatShell>
  );
};

/* ── Beat 4 · Biochemistry ────────────────────────────────────────────────── */
export const BiochemBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an interpretation.");
      setRes({ earned: optPts(c.biochem.options[idx].score, 20, 10), max: 20, detail: "Biochemistry interpretation", key: "biochem", label: "Biochemistry" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={FlaskConical}
      eyebrow="Stage 4 of 5 · Biochemistry"
      title="Biochemistry interpretation"
      subtitle={c.biochem.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="biochem-primary-btn"
    >
      <ResultsTable rows={c.biochem.panel} testid="biochem-panel" />
      <OptionList options={c.biochem.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="biochem-option" />
    </BeatShell>
  );
};

/* ── Beat 5 · Action / report ─────────────────────────────────────────────── */
export const ActionBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an action.");
      setRes({ earned: optPts(c.action.options[idx].score, 20, 10), max: 20, detail: "Reporting & escalation", key: "action", label: "Action / report" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={MessageSquare}
      eyebrow="Stage 5 of 5 · Action"
      title="Action & reporting"
      subtitle={c.action.prompt}
      primaryLabel={checked ? "See debrief →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="action-primary-btn"
    >
      <OptionList options={c.action.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="action-option" />
    </BeatShell>
  );
};
